import { useQuery } from "@apollo/client";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";

import { ResourceName } from "../constants/resources";
import {
  type SalesforceReportDashboard,
  SalesforceReportDashboardDocument,
  type SalesforceReportDashboardQuery,
  type SalesforceReportDashboardQueryVariables,
  type SalesforceReportSectionFragment,
} from "../generated/graphql";
import { SALESFORCE_CHART_COLORS, salesforceSyncedAtFormat } from "../lib/salesforceDashboard";
import { type ReportComponent, type ReportContext, salesforceReportUrl } from "../lib/salesforceReport";
import { color, space } from "../theme/designTokens";
import { ListPagination } from "./ListPagination";
import { SalesforceDashboardCard } from "./SalesforceDashboardCharts";
import { ReportCoverageWarnings } from "./SalesforceReportAvailability";
import { SalesforceReportChartBody } from "./SalesforceReportChartBody";
import { ReportTableView } from "./SalesforceReportTableView";

type ReportDashboard = SalesforceReportDashboardQuery["salesforceReportDashboard"];

const colors = SALESFORCE_CHART_COLORS;
const ICON_TILE_SIZE_PX = 32;
const WARNING_ICON_SIZE_PX = 16;
const GRID_COLUMNS = 12;
const DASHBOARD_TABLE_PAGE_SIZE = 10;
const GRID_ROW_HEIGHT_PX = 36;

const Explanation = ({ children }: { children: string }) => (
  <Box
    sx={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      p: `${space.xl}px`,
      fontSize: "12px",
      textAlign: "center",
      color: colors.textWeak,
    }}
  >
    {children}
  </Box>
);

const CardNotice = ({ children }: { children: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: `${space.sm}px`, mb: `${space.sm}px`, fontSize: "12px" }}>
    <WarningAmberOutlinedIcon
      aria-hidden
      sx={{
        flex: "none",
        width: `${WARNING_ICON_SIZE_PX}px`,
        height: `${WARNING_ICON_SIZE_PX}px`,
        color: colors.warning,
      }}
    />
    {children}
  </Box>
);

const ReportTableTile = ({
  table,
  reportId,
  slug,
  context,
  recordLinks,
}: {
  table: SalesforceReportSectionFragment;
  reportId: string;
  slug: string;
  context: ReportContext;
  recordLinks: boolean;
}) => {
  const translate = useTranslate();
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [table, context, slug]);
  return (
    <Box sx={{ overflow: "auto", minHeight: 0 }}>
      <ReportTableView
        page={{
          ...table,
          rows: table.rows.slice((page - 1) * DASHBOARD_TABLE_PAGE_SIZE, page * DASHBOARD_TABLE_PAGE_SIZE),
        }}
        options={{ rowCounts: false, detailRows: true, subtotals: false, grandTotal: false, recordLinks }}
        filterPath={(field, value) =>
          salesforceReportUrl(reportId, { dashboard: slug, columnFilters: [{ field, value }] }, context)
        }
      />
      <ListPagination
        page={page}
        perPage={DASHBOARD_TABLE_PAGE_SIZE}
        resource={ResourceName.LogisticsReports}
        total={table.rows.length}
        onPageChange={setPage}
      />
      {table.hasMoreRows && (
        <Alert severity="info">{translate("salesforceReports.report.rowLimit", { limit: table.rowLimit })}</Alert>
      )}
    </Box>
  );
};

const ReportCard = ({
  component,
  asOf,
  slug,
  context,
  recordLinks,
}: {
  component: ReportComponent;
  asOf: string;
  slug: string;
  context: ReportContext;
  recordLinks: boolean;
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { layout, unavailable, unreadableFields } = component;
  const textKey = `salesforceReports.components.${component.key}`;
  const subtitle = translate(`${textKey}.subtitle`, { _: "" });

  return (
    <Box
      data-testid={`salesforce-report-${component.key}`}
      style={{
        gridColumn: `${layout.column + 1} / span ${layout.width}`,
        gridRow: `${layout.row + 1} / span ${layout.height}`,
      }}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: "minmax(0, 1fr)",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <SalesforceDashboardCard
        asOf={asOf}
        definition={translate(`${textKey}.footer`, { _: "" })}
        fillHeight
        link={
          unavailable
            ? undefined
            : {
                to: salesforceReportUrl(component.reportId, { dashboard: slug }, context),
                label: translate("salesforceReports.report.viewReport"),
              }
        }
        subtitle={subtitle === "" ? undefined : subtitle}
        title={translate(`${textKey}.title`)}
      >
        <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          <ReportCoverageWarnings missingFields={component.missingFields} pendingObjects={component.pendingObjects} />
          {unreadableFields.length > 0 && (
            <CardNotice>
              {translate("salesforceReports.unreadableFields", { fields: unreadableFields.join(", ") })}
            </CardNotice>
          )}
          {unavailable ? (
            <Explanation>{translate(`salesforceReports.unavailable.${unavailable}`)}</Explanation>
          ) : component.table ? (
            <ReportTableTile
              table={component.table}
              reportId={component.reportId}
              slug={slug}
              context={context}
              recordLinks={recordLinks}
            />
          ) : (
            <SalesforceReportChartBody
              component={component}
              onSelect={values =>
                navigate(
                  salesforceReportUrl(
                    component.reportId,
                    { component: component.key, values, dashboard: slug },
                    context
                  )
                )
              }
            />
          )}
        </Box>
      </SalesforceDashboardCard>
    </Box>
  );
};

const ReportGrid = ({
  dashboard,
  asOf,
  slug,
  context,
  recordLinks,
}: {
  dashboard: ReportDashboard;
  asOf: string;
  slug: string;
  context: ReportContext;
  recordLinks: boolean;
}) => (
  <Box
    sx={{
      display: "grid",
      gap: `${space.md}px`,
      gridAutoRows: `${GRID_ROW_HEIGHT_PX}px`,
      gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
    }}
  >
    {dashboard.components.map(component => (
      <ReportCard
        asOf={asOf}
        component={component}
        key={component.key}
        slug={slug}
        context={context}
        recordLinks={recordLinks}
      />
    ))}
  </Box>
);

export const SalesforceReportDashboardContent = ({
  dashboard: dashboardId,
  slug,
  children,
  context = "general",
  embedded = false,
}: {
  dashboard: SalesforceReportDashboard;
  slug: string;
  children?: ReactNode;
  context?: ReportContext;
  embedded?: boolean;
}) => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const recordLinks = context !== "logistics" || (canAccess === true && !isPending);
  const { data, loading, error, refetch } = useQuery<
    SalesforceReportDashboardQuery,
    SalesforceReportDashboardQueryVariables
  >(SalesforceReportDashboardDocument, {
    variables: { dashboard: dashboardId },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const dashboard = data?.salesforceReportDashboard;
  const name = translate(`salesforceReports.dashboards.${dashboardId}`);
  const syncedAt = dashboard?.lastSyncedAt ? salesforceSyncedAtFormat.format(new Date(dashboard.lastSyncedAt)) : null;
  const asOf = syncedAt === null ? "" : translate("salesforceDashboard.asOf", { date: syncedAt });

  return (
    <Box sx={{ p: embedded ? 0 : `${space.xl}px`, mt: embedded ? `${space.xl}px` : 0 }}>
      {!embedded && <Title title={name} />}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: `${space.xl}px`,
          mb: `${space.lg}px`,
          p: `${space.lg}px ${space.xl}px`,
          backgroundColor: colors.headerBackground,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: "4px",
          color: colors.text,
        }}
      >
        <Box sx={{ display: "flex", gap: `${space.lg}px`, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              display: "flex",
              flex: "none",
              alignItems: "center",
              justifyContent: "center",
              width: `${ICON_TILE_SIZE_PX}px`,
              height: `${ICON_TILE_SIZE_PX}px`,
              backgroundColor: color.primary,
              borderRadius: "4px",
              color: color.white,
            }}
          >
            <SpeedOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.xs}px`, minWidth: 0 }}>
            <Typography sx={{ fontSize: "12px", lineHeight: 1.25, color: colors.text }}>
              {translate("salesforceReports.overline")}
            </Typography>
            <Typography
              component={embedded ? "h2" : "h1"}
              sx={{ m: 0, fontSize: "18px", fontWeight: 700, lineHeight: 1.25, color: colors.text }}
            >
              {name}
            </Typography>
            {dashboard?.lastSyncedAt && <Typography sx={{ fontSize: "12px", color: colors.text }}>{asOf}</Typography>}
          </Box>
        </Box>
        <Button
          disabled={loading}
          onClick={() => void refetch().catch(() => undefined)}
          startIcon={<RefreshOutlinedIcon />}
          sx={{
            height: "32px",
            backgroundColor: colors.cardBackground,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: "4px",
            fontSize: "13px",
            textTransform: "none",
            color: colors.link,
            "&:hover": { backgroundColor: colors.headerBackground, border: `1px solid ${colors.cardBorder}` },
          }}
          variant="outlined"
        >
          {translate("salesforceDashboard.refresh")}
        </Button>
      </Box>

      {children}

      {loading && !dashboard && <CircularProgress aria-label={translate("salesforceDashboard.loading")} />}
      {error && (
        <Alert severity="error" sx={{ mb: `${space.lg}px` }}>
          {translate("salesforceDashboard.loadError")}
        </Alert>
      )}

      {context !== "logistics" && dashboard?.lastSyncedAt === null && (
        <Paper variant="outlined" sx={{ p: `${space.xl}px` }}>
          <Typography component="h2" variant="h6">
            {translate("salesforceDashboard.noImportTitle")}
          </Typography>
          <Typography color="text.secondary">{translate("salesforceDashboard.noImportDescription")}</Typography>
        </Paper>
      )}

      {dashboard && (dashboard.lastSyncedAt || context === "logistics") && (
        <ReportGrid asOf={asOf} dashboard={dashboard} slug={slug} context={context} recordLinks={recordLinks} />
      )}
    </Box>
  );
};
